import { UserDetailsResponse } from '@fullstory/client-api';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { EventSearch } from '@fullstory/proto/fs/api/search';
import {
  createGRPCAction,
  createRequestAction,
  RequestAction,
  RequestActionType,
  ActionType,
  createAction,
} from '@fullstory/redux-util';
import { UserOverlayTypes } from './types';
import { RequestType } from '@fullstory/util/xhr';
import jstz from 'jstz';
import { IndividualRequest, UserRequest, EraserPublic } from '@fullstory/proto/fs/services/eraser';

export const UserOverlayActions = {
  setSegmentDefinition: (
    segmentDefinition: SegmentDefinition | null,
    eventSearch?: EventSearch | null,
  ) =>
    createAction({
      type: UserOverlayTypes.SetSegmentDefinition,
      segmentDefinition,
      eventSearch,
    }),
};

const makeUserDetailsRequest = (
  type: UserOverlayTypes,
  userID: string,
  segmentDefinition: SegmentDefinition,
  eventSearch?: EventSearch,
  force?: boolean,
): RequestAction<UserOverlayTypes, SegmentDefinition | EventSearch> => ({
  type,
  buildRequest: {
    url: '/userDetails',
    requestType: RequestType.PostJson,
    params: {
      Tz: jstz.determine().name(),
      IndvId: userID,
    },
    headers: eventSearch ? { 'X-Use-Event-Search': '1' } : { 'X-Use-Segment-Definition': '1' },
  },
  readWrite: 'write',
  force,
  body: eventSearch ?? segmentDefinition,
});
export const UserOverlayRequestActions = {
  eraserDeleteIndv: (body: IndividualRequest) =>
    createGRPCAction<typeof EraserPublic.DeleteIndividual>()(
      {
        type: UserOverlayTypes.DeleteUserRequest,
        grpcFunction: EraserPublic.DeleteIndividual,
        body,
        readWrite: 'write',
      },
      UserOverlayTypes.DeleteUserSuccess,
      UserOverlayTypes.DeleteUserFailure,
    ),
  eraserDeleteUser: (body: UserRequest) =>
    createGRPCAction<typeof EraserPublic.DeleteUser>()(
      {
        type: UserOverlayTypes.DeleteUserRequest,
        grpcFunction: EraserPublic.DeleteUser,
        body,
        readWrite: 'write',
      },
      UserOverlayTypes.DeleteUserSuccess,
      UserOverlayTypes.DeleteUserFailure,
    ),
  eraserQueryIndv: (body: IndividualRequest) =>
    createGRPCAction<typeof EraserPublic.QueryIndividual>()(
      {
        type: UserOverlayTypes.QueryUserRequest,
        grpcFunction: EraserPublic.QueryIndividual,
        body,
        readWrite: 'write',
      },
      UserOverlayTypes.QueryUserSuccess,
      UserOverlayTypes.QueryUserFailure,
    ),
  eraserQueryUser: (body: UserRequest) =>
    createGRPCAction<typeof EraserPublic.QueryUser>()(
      {
        type: UserOverlayTypes.QueryUserRequest,
        grpcFunction: EraserPublic.QueryUser,
        body,
        readWrite: 'write',
      },
      UserOverlayTypes.QueryUserSuccess,
      UserOverlayTypes.QueryUserFailure,
    ),
  matchingSessionsOrClips: (
    userID: string,
    segmentDefinition: SegmentDefinition,
    eventSearch?: EventSearch,
    force?: boolean,
  ) =>
    createRequestAction<UserDetailsResponse>()(
      makeUserDetailsRequest(
        UserOverlayTypes.UserDetailsMatchingRequest,
        userID,
        segmentDefinition,
        eventSearch,
        force,
      ),
      UserOverlayTypes.UserDetailsMatchingSuccess,
      UserOverlayTypes.UserDetailsMatchingFailure,
    ),
  allSessions: (userID: string, segmentDefinition: SegmentDefinition, force?: boolean) =>
    createRequestAction<UserDetailsResponse>()(
      makeUserDetailsRequest(
        UserOverlayTypes.UserDetailsAllSessionsRequest,
        userID,
        segmentDefinition,
        undefined,
        force,
      ),
      UserOverlayTypes.UserDetailsAllSessionsSuccess,
      UserOverlayTypes.UserDetailsAllSessionsFailure,
    ),
};

export type UserOverlayActions = ActionType<typeof UserOverlayActions>;

export type UserOverlayRequestActions = RequestActionType<typeof UserOverlayRequestActions>;
